import React, { useState, useContext, useEffect, useRef } from 'react';
import { AirDropContext } from '../../../utils/AirDropProvider';
import useLocale from '../../hooks/useLocale';
import GlobeIcon from '../GlobeIcon/GlobeIcon';
import ExpandIcon from '../ExpandIcon/ExpandIcon';
import CollapseIcon from '../CollapseIcon/CollapseIcon';
import useTranslation from '../../hooks/useTranslation';

interface DisplayLang {
  text: string;
  value: string;
}

function LangPicker() {
  const popoverRef: any = useRef(null);
  const buttonRef: any = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('');
  const [displayLangs, setDisplayLangs] = useState<Array<DisplayLang>>([]);
  const { translateText } = useTranslation();

  const config : any = useContext(AirDropContext);
  const languagePickerLanguages: Array<DisplayLang> = config?.languagePickerLanguages;

  const { getLocale, getHyphenatedLocale, setLocale } = useLocale();

  useEffect(() => {
    const dLangs: Array<DisplayLang> = languagePickerLanguages.filter(
      (lang : DisplayLang) => (config?.languagePicker?.locales?.includes(lang.value) ? lang : undefined),
    );
    setDisplayLangs(dLangs);

    // Find matching language entry given user's locale. Special case for 'de_AT' (informal German) which we
    // don't want in the picker. 'de' will be selected in the picker for 'de_AT', but de_AT translations
    // will still be applied. Changing the picker to reselect 'de' will show the page in normal German.
    // This is a hack, but what the business wants.
    const locale = getLocale();
    const match = languagePickerLanguages.find((l: any) => l.value === locale);
    setSelectedLang(match ? match.text : '');
  }, []);

  const toggleLangPicker = () => {
    setIsOpen(!isOpen);
  };

  const onClickOutside = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!popoverRef?.current.contains(event.target) && !buttonRef?.current.contains(event.target)) {
        onClickOutside();
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [onClickOutside]);

  const langChanged = (e: any) => {
    const match = languagePickerLanguages.find((l: any) => l.value === e.target.value);
    setSelectedLang(match ? match.text : '');
    setLocale(e.target.value, true); /* flag to add lang to url */
  };

  return (
    <>
      <div className="lang-picker-controls">
        <div className="globe-icon">
          <GlobeIcon aria-hidden />
        </div>
        <div className="language-label">
          {selectedLang}
        </div>
        <button
          type="button"
          ref={buttonRef}
          id="lang-picker-trigger"
          className="pe-icon--btn lang-picker-icon"
          aria-label={`Language, ${selectedLang}`}
          aria-labelledby="svg-title-lang-picker-toggle"
          title="Select your language"
          aria-haspopup="true"
          aria-controls="#lang-picker-popover"
          onClick={toggleLangPicker}
        >
          <svg
            role="img"
            focusable="false"
            className="expand-collapse-icons"
            aria-labelledby="svg-title-lang-picker-toggle"
          >
            <title id="svg-title-lang-picker-toggle">{translateText('Select your language')}</title>
            {isOpen ? <CollapseIcon /> : <ExpandIcon />}
          </svg>
        </button>
      </div>

      <div
        ref={popoverRef}
        className={`lang-picker-popover ${isOpen ? 'open' : 'closed'}`}
      >
        <h1 className="heading">{translateText('Language')}</h1>
        <div className="sr-only" id="radio-buttons-description">{translateText('Select your language')}</div>
        <ul role="radiogroup" aria-describedby="radio-buttons-description">
          {displayLangs.map(({ text, value }) => (
            <li key={value}>
              <label className="ies-radio-label" htmlFor={`${value}-radio-btn`} lang={getHyphenatedLocale(value)}>
                <input
                  type="radio"
                  name="languages"
                  value={value}
                  checked={text === selectedLang}
                  onChange={(e) => {
                    langChanged(e);
                  }}
                  className="ies-radio"
                  id={`${value}-radio-btn`}
                />
                {text}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export default LangPicker;
